import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, MapPin, Briefcase, Sparkles, Loader2, SlidersHorizontal, BellPlus, Globe2, Building2, Home as HomeIcon, DollarSign, Clock, Star, Languages } from "lucide-react";
import { motion } from "framer-motion";
import CompanyLogo from "@/components/CompanyLogo";
import MatchBadge from "@/components/MatchBadge";
import { toast } from "sonner";

function cleanText(html) {
  if (!html) return "";
  // Decode entities (Arbeitnow returns double-escaped `&lt;div&gt;` etc.)
  const ta = document.createElement("textarea");
  ta.innerHTML = html;
  let decoded = ta.value;
  if (decoded.includes("&lt;") || decoded.includes("&amp;")) {
    ta.innerHTML = decoded;
    decoded = ta.value;
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = decoded;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
}

// Curated popular countries for the dropdown. "" = any.
const COUNTRIES = [
  "United States", "United Kingdom", "Canada", "Germany", "France", "Netherlands",
  "Spain", "Portugal", "Ireland", "Sweden", "Denmark", "Norway", "Finland",
  "Switzerland", "Austria", "Belgium", "Italy", "Poland", "Czech Republic",
  "Australia", "New Zealand", "Singapore", "India", "Japan", "Brazil",
  "Mexico", "Argentina", "United Arab Emirates", "South Africa",
];

// Curated timezone list (IANA). "" = no filter.
const TIMEZONES = [
  { label: "PT · Los Angeles (UTC-8/-7)", value: "America/Los_Angeles" },
  { label: "MT · Denver (UTC-7/-6)", value: "America/Denver" },
  { label: "CT · Chicago (UTC-6/-5)", value: "America/Chicago" },
  { label: "ET · New York (UTC-5/-4)", value: "America/New_York" },
  { label: "BRT · São Paulo (UTC-3)", value: "America/Sao_Paulo" },
  { label: "GMT/BST · London", value: "Europe/London" },
  { label: "CET/CEST · Berlin, Paris, Amsterdam", value: "Europe/Berlin" },
  { label: "EET · Athens, Helsinki", value: "Europe/Athens" },
  { label: "GST · Dubai (UTC+4)", value: "Asia/Dubai" },
  { label: "IST · Bangalore, Mumbai (UTC+5:30)", value: "Asia/Kolkata" },
  { label: "SGT · Singapore (UTC+8)", value: "Asia/Singapore" },
  { label: "JST · Tokyo (UTC+9)", value: "Asia/Tokyo" },
  { label: "AEST/AEDT · Sydney", value: "Australia/Sydney" },
  { label: "NZST · Auckland", value: "Pacific/Auckland" },
];

// City / country → IANA timezone (mirrors backend CITY_TZ + COUNTRY_TZ). Used to auto-suggest
// a timezone when a user picks a location, so they never over-filter by accident.
const CITY_TZ_HINT = {
  // US
  "san francisco": "America/Los_Angeles", "los angeles": "America/Los_Angeles",
  "seattle": "America/Los_Angeles", "portland": "America/Los_Angeles",
  "san diego": "America/Los_Angeles", "phoenix": "America/Denver",
  "denver": "America/Denver", "chicago": "America/Chicago", "austin": "America/Chicago",
  "dallas": "America/Chicago", "houston": "America/Chicago", "minneapolis": "America/Chicago",
  "new york": "America/New_York", "boston": "America/New_York", "washington": "America/New_York",
  "atlanta": "America/New_York", "miami": "America/New_York", "philadelphia": "America/New_York",
  // Canada
  "vancouver": "America/Los_Angeles", "calgary": "America/Denver",
  "toronto": "America/New_York", "ottawa": "America/New_York", "montreal": "America/New_York",
  // Europe
  "london": "Europe/London", "dublin": "Europe/London", "lisbon": "Europe/London",
  "amsterdam": "Europe/Berlin", "berlin": "Europe/Berlin", "munich": "Europe/Berlin",
  "paris": "Europe/Berlin", "madrid": "Europe/Berlin", "barcelona": "Europe/Berlin",
  "rome": "Europe/Berlin", "milan": "Europe/Berlin", "stockholm": "Europe/Berlin",
  "copenhagen": "Europe/Berlin", "zurich": "Europe/Berlin", "vienna": "Europe/Berlin",
  "warsaw": "Europe/Berlin", "athens": "Europe/Athens", "helsinki": "Europe/Athens",
  "istanbul": "Europe/Athens",
  // APAC / EMEA
  "singapore": "Asia/Singapore", "hong kong": "Asia/Singapore", "shanghai": "Asia/Singapore",
  "tokyo": "Asia/Tokyo", "seoul": "Asia/Tokyo",
  "sydney": "Australia/Sydney", "melbourne": "Australia/Sydney", "brisbane": "Australia/Sydney",
  "auckland": "Pacific/Auckland", "dubai": "Asia/Dubai",
  "tel aviv": "Europe/Athens",
  "bangalore": "Asia/Kolkata", "mumbai": "Asia/Kolkata", "delhi": "Asia/Kolkata",
  "hyderabad": "Asia/Kolkata", "chennai": "Asia/Kolkata", "pune": "Asia/Kolkata",
  // LatAm
  "sao paulo": "America/Sao_Paulo", "são paulo": "America/Sao_Paulo",
  "buenos aires": "America/Sao_Paulo", "santiago": "America/Sao_Paulo",
  "mexico city": "America/Chicago", "bogota": "America/New_York",
};
const COUNTRY_TZ_HINT = {
  "united states": "America/New_York", "canada": "America/New_York",
  "united kingdom": "Europe/London", "ireland": "Europe/London",
  "germany": "Europe/Berlin", "france": "Europe/Berlin", "netherlands": "Europe/Berlin",
  "spain": "Europe/Berlin", "portugal": "Europe/London", "italy": "Europe/Berlin",
  "sweden": "Europe/Berlin", "denmark": "Europe/Berlin", "switzerland": "Europe/Berlin",
  "austria": "Europe/Berlin", "poland": "Europe/Berlin", "czech republic": "Europe/Berlin",
  "belgium": "Europe/Berlin", "norway": "Europe/Berlin", "finland": "Europe/Athens",
  "greece": "Europe/Athens", "united arab emirates": "Asia/Dubai",
  "singapore": "Asia/Singapore", "india": "Asia/Kolkata", "japan": "Asia/Tokyo",
  "australia": "Australia/Sydney", "new zealand": "Pacific/Auckland",
  "brazil": "America/Sao_Paulo", "argentina": "America/Sao_Paulo",
  "mexico": "America/Chicago", "south africa": "Europe/Athens",
};

// Languages the user can toggle on/off. English is implicit — always assumed.
const LANGUAGE_OPTIONS = [
  "German", "French", "Spanish", "Italian", "Portuguese", "Dutch", "Polish",
  "Swedish", "Danish", "Norwegian", "Finnish", "Russian", "Ukrainian",
  "Chinese", "Japanese", "Korean", "Arabic", "Hebrew", "Turkish", "Greek",
  "Czech", "Hungarian", "Romanian", "Thai", "Vietnamese", "Hindi",
];

// Map browser BCP-47 language tags (primary subtag, lowercased) → chip name in LANGUAGE_OPTIONS.
const BROWSER_LANG_MAP = {
  de: "German", fr: "French", es: "Spanish", it: "Italian",
  pt: "Portuguese", nl: "Dutch", pl: "Polish", sv: "Swedish",
  da: "Danish", no: "Norwegian", nb: "Norwegian", nn: "Norwegian",
  fi: "Finnish", ru: "Russian", uk: "Ukrainian",
  zh: "Chinese", ja: "Japanese", ko: "Korean", ar: "Arabic",
  he: "Hebrew", iw: "Hebrew", tr: "Turkish", el: "Greek",
  cs: "Czech", hu: "Hungarian", ro: "Romanian",
  th: "Thai", vi: "Vietnamese", hi: "Hindi",
};

function detectBrowserLanguages() {
  try {
    const list = Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator.language || "en"];
    const out = new Set();
    for (const tag of list) {
      const primary = String(tag || "").toLowerCase().split(/[-_]/)[0];
      if (primary === "en") continue; // English is always implicit
      const mapped = BROWSER_LANG_MAP[primary];
      if (mapped && LANGUAGE_OPTIONS.includes(mapped)) out.add(mapped);
    }
    return Array.from(out);
  } catch { return []; }
}

function suggestTz(city, country) {
  const c = (city || "").trim().toLowerCase();
  if (c && CITY_TZ_HINT[c]) return CITY_TZ_HINT[c];
  const co = (country || "").trim().toLowerCase();
  if (co && COUNTRY_TZ_HINT[co]) return COUNTRY_TZ_HINT[co];
  return "";
}

export default function Jobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("");
  const [workType, setWorkType] = useState(""); // "" = All, "remote"
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [minSalary, setMinSalary] = useState(0); // USD
  const [freshDays, setFreshDays] = useState(0); // 0 = any, 1/7/30
  const [userTz, setUserTz] = useState(""); // e.g. "America/New_York"; "" = no filter
  const [tzDelta, setTzDelta] = useState(6);
  const [tzManualOverride, setTzManualOverride] = useState(false); // don't auto-swap once user picks
  const [spokenLangs, setSpokenLangs] = useState(() => {
    // Priority 1: previously saved selection (returning users)
    try {
      const saved = localStorage.getItem("cah_spoken_langs");
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed.filter((l) => LANGUAGE_OPTIONS.includes(l));
      }
    } catch { /* noop */ }
    // Priority 2: auto-detect from the browser's language list
    return detectBrowserLanguages();
  });
  const [includeAllLangs, setIncludeAllLangs] = useState(false); // if true, show roles in any language (default: English + speakers-only)
  const [watchlist, setWatchlist] = useState(new Set()); // set of company_key
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [minScore, setMinScore] = useState(0);
  const [savingSearch, setSavingSearch] = useState(false);

  const load = async (p = 1, q = query, t = tag, wt = workType, cn = country, ct = city, sal = minSalary, fresh = freshDays, tz = userTz, tzd = tzDelta, langs = spokenLangs, allLangs = includeAllLangs) => {
    setLoading(true);
    try {
      const langsParam = (langs && langs.length) ? langs.join(",") : "";
      const { data } = await api.get("/jobs", {
        params: { page: p, search: q, tag: t, work_type: wt, country: cn, city: ct, min_salary: sal, posted_within_days: fresh, tz, tz_delta: tzd, languages: langsParam, include_all_languages: allLangs ? 1 : 0 },
      });
      setJobs(data.jobs || []);
      setTotal(data.total ?? (data.jobs || []).length);
      setHasMore(!!data.has_more);
      setPage(p);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    // Auto-detect user's browser timezone as the default
    try {
      const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (guess) setUserTz(""); // don't apply by default; just pre-populate the selector below
      window.__browserTz = guess;
    } catch { /* noop */ }
    // If we auto-detected languages from the browser (no prior save), let the user know once.
    try {
      const saved = localStorage.getItem("cah_spoken_langs");
      if (saved === null && spokenLangs.length > 0) {
        toast.success(`Auto-added ${spokenLangs.join(", ")} from your browser. Untick anything you don't speak.`);
      }
    } catch { /* noop */ }
    load(1, "", "", "", "", "", 0, 0, "", 6, spokenLangs);
  }, []);

  // Persist language selection so returning users keep it (and so first-time detection isn't repeated).
  useEffect(() => {
    try { localStorage.setItem("cah_spoken_langs", JSON.stringify(spokenLangs)); } catch { /* noop */ }
  }, [spokenLangs]);

  useEffect(() => {
    if (!user) { setHasResume(false); return; }
    api.get("/resume").then(({ data }) => setHasResume(!!(data.text && data.text.length > 20))).catch(() => setHasResume(false));
    api.get("/watchlist").then(({ data }) => setWatchlist(new Set((data.items || []).map((i) => i.company_key)))).catch(() => setWatchlist(new Set()));
  }, [user]);

  const companyKey = (c) => (c || "").trim().replace(/\s+/g, " ").toLowerCase();
  const toggleWatch = async (company) => {
    if (!user) { toast.error("Log in to star companies"); return; }
    const key = companyKey(company);
    if (!key) return;
    if (watchlist.has(key)) {
      await api.delete(`/watchlist/${encodeURIComponent(key)}`);
      const next = new Set(watchlist); next.delete(key); setWatchlist(next);
      toast.success(`Removed ${company} from watchlist`);
    } else {
      await api.post("/watchlist", { company });
      setWatchlist(new Set([...watchlist, key]));
      toast.success(`Starred ${company} — we'll ping you when they post`);
    }
  };

  const commonTags = ["remote", "engineering", "design", "product", "marketing", "sales", "support"];

  const runSearch = () => load(1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta);

  const clearLocation = () => {
    setCountry(""); setCity(""); setWorkType("");
    load(1, query, tag, "", "", "", minSalary, freshDays, userTz, tzDelta);
  };

  const toggleLang = (lang) => {
    const next = spokenLangs.includes(lang)
      ? spokenLangs.filter((l) => l !== lang)
      : [...spokenLangs, lang];
    setSpokenLangs(next);
    load(1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta, next);
  };

  const saveSearch = async () => {
    if (!user) { toast.error("Log in to save searches"); return; }
    if (!query && !tag && !workType && !country && !city && !minSalary && !freshDays && !userTz && !spokenLangs.length) { toast.error("Add a filter first"); return; }
    const suggested = [query, tag, workType, city, country, minSalary ? `$${minSalary/1000}k+` : "", freshDays ? `${freshDays}d` : "", userTz ? `TZ ±${tzDelta}h` : "", spokenLangs.length ? `speak ${spokenLangs.length}` : ""].filter(Boolean).join(" · ") || "My alert";
    const name = window.prompt("Name this alert (used in your weekly email):", suggested);
    if (!name) return;
    setSavingSearch(true);
    try {
      await api.post("/saved-searches", { name, query, tag, min_score: minScore, work_type: workType, country, city, min_salary: minSalary, posted_within_days: freshDays, tz: userTz, tz_delta: tzDelta, languages: spokenLangs });
      toast.success("Saved — you'll get a weekly email digest");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingSearch(false); }
  };

  const visibleJobs = hasResume && minScore > 0
    ? jobs.filter((j) => (j.match_score ?? 0) >= minScore)
    : jobs;
  // Sort by score if we have it
  const sortedJobs = hasResume
    ? [...visibleJobs].sort((a, b) => (b.match_score ?? 0) - (a.match_score ?? 0))
    : visibleJobs;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Fresh remote jobs</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Find your next role</h1>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Sparkles className="w-4 h-4 text-[#eab308]" /> Aggregated from Arbeitnow, RemoteOK & The Muse
        </div>
      </div>

      <div className="nb-card p-4 md:p-5 mb-6 bg-white">
        {/* Row 1: keyword + primary actions */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 border-2 border-black rounded-lg px-3">
            <Search className="w-4 h-4" />
            <input
              data-testid="jobs-search"
              value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Search title, company, keywords…"
              className="w-full py-2.5 outline-none bg-transparent"
            />
          </div>
          <button data-testid="jobs-search-btn" onClick={runSearch} className="nb-btn bg-[#4338ca] text-white px-5 py-2.5">Search</button>
          {user && (
            <button data-testid="save-search-btn" onClick={saveSearch} disabled={savingSearch}
              className="nb-btn bg-[#eab308] text-black px-4 py-2.5 inline-flex items-center gap-2">
              {savingSearch ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellPlus className="w-4 h-4" />} Save alert
            </button>
          )}
        </div>

        {/* Row 2: location + work type */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[auto,1fr,1fr,auto] gap-3 items-center">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-600">
            <MapPin className="w-3.5 h-3.5" /> Location
          </div>
          <div className="flex items-center gap-2 border-2 border-black rounded-lg px-3">
            <Globe2 className="w-4 h-4" />
            <select
              data-testid="jobs-country"
              value={country}
              onChange={(e) => {
                const cn = e.target.value;
                setCountry(cn);
                // Auto-suggest tz — but never override an explicit user pick
                let tzNext = userTz;
                if (!tzManualOverride) {
                  const suggested = suggestTz(city, cn);
                  if (suggested && suggested !== userTz) {
                    tzNext = suggested;
                    setUserTz(suggested);
                    const label = TIMEZONES.find((t) => t.value === suggested)?.label || suggested;
                    toast.message(`Timezone set to ${label.split(" · ")[0]}`, { description: "Change or clear it in the timezone dropdown." });
                  }
                }
                load(1, query, tag, workType, cn, city, minSalary, freshDays, tzNext, tzDelta);
              }}
              className="w-full py-2.5 outline-none bg-transparent font-medium"
            >
              <option value="">Any country</option>
              {COUNTRIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-2 border-2 border-black rounded-lg px-3">
            <Building2 className="w-4 h-4" />
            <input
              data-testid="jobs-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onBlur={() => {
                // Suggest tz when the user leaves the city input (avoid firing on every keystroke)
                if (!tzManualOverride) {
                  const suggested = suggestTz(city, country);
                  if (suggested && suggested !== userTz) {
                    setUserTz(suggested);
                    const label = TIMEZONES.find((t) => t.value === suggested)?.label || suggested;
                    toast.message(`Timezone set to ${label.split(" · ")[0]}`, { description: "Change or clear it in the timezone dropdown." });
                    load(1, query, tag, workType, country, city, minSalary, freshDays, suggested, tzDelta);
                  }
                }
              }}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="City (e.g. Berlin, London, Austin)"
              className="w-full py-2.5 outline-none bg-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="work-type-all"
              onClick={() => { setWorkType(""); load(1, query, tag, "", country, city, minSalary, freshDays, userTz, tzDelta); }}
              className={`nb-pill ${workType === "" ? "bg-black text-white" : "bg-white"}`}
            >All</button>
            <button
              data-testid="work-type-remote"
              onClick={() => { setWorkType("remote"); load(1, query, tag, "remote", country, city, minSalary, freshDays, userTz, tzDelta); }}
              className={`nb-pill inline-flex items-center gap-1 ${workType === "remote" ? "bg-[#22c55e] text-white" : "bg-white"}`}
            ><HomeIcon className="w-3 h-3" /> Remote only</button>
          </div>
        </div>

        {(country || city || workType || minSalary > 0 || freshDays > 0 || userTz || spokenLangs.length > 0 || includeAllLangs) && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-gray-500 font-bold uppercase tracking-widest">Active filters:</span>
            {workType === "remote" && <span className="nb-pill bg-[#22c55e] text-white">Remote only</span>}
            {country && <span className="nb-pill bg-[#4338ca] text-white">{country}</span>}
            {city && <span className="nb-pill bg-[#ec4899] text-white">{city}</span>}
            {minSalary > 0 && <span className="nb-pill bg-[#0caa41] text-white">${(minSalary/1000).toFixed(0)}k+</span>}
            {freshDays > 0 && <span className="nb-pill bg-[#eab308] text-black">Last {freshDays}d</span>}
            {userTz && <span className="nb-pill bg-[#4338ca] text-white">{userTz.split("/").pop().replace(/_/g," ")} ±{tzDelta}h</span>}
            {includeAllLangs
              ? <span className="nb-pill bg-black text-white">Any language</span>
              : (spokenLangs.length > 0
                ? <span className="nb-pill bg-[#22c55e] text-white">Speak: EN + {spokenLangs.join(", ")}</span>
                : <span className="nb-pill bg-[#22c55e] text-white">English only</span>)}
            <button data-testid="clear-location" onClick={clearLocation} className="nb-pill bg-white">clear location</button>
            {!loading && <span className="ml-auto text-gray-600" data-testid="jobs-total">{total} match{total === 1 ? "" : "es"} worldwide</span>}
          </div>
        )}

        {/* Row 3: salary + freshness */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[auto,1fr,auto] gap-3 md:items-center">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-600">
            <DollarSign className="w-3.5 h-3.5" /> Min salary
          </div>
          <div className="flex items-center gap-3">
            <input
              data-testid="min-salary-slider"
              type="range" min="0" max="250000" step="10000"
              value={minSalary}
              onChange={(e) => setMinSalary(parseInt(e.target.value, 10))}
              onMouseUp={() => load(1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta)}
              onTouchEnd={() => load(1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta)}
              onKeyUp={(e) => (e.key === "ArrowRight" || e.key === "ArrowLeft") && load(1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta)}
              className="w-full accent-[#0caa41]"
            />
            <span data-testid="min-salary-label" className="text-sm font-bold w-24 text-right">
              {minSalary === 0 ? "Any" : `$${(minSalary / 1000).toFixed(0)}k+`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-gray-600 mr-1">
              <Clock className="w-3.5 h-3.5" /> Posted
            </div>
            {[{v: 0, l: "Any"}, {v: 1, l: "24h"}, {v: 7, l: "7d"}, {v: 30, l: "30d"}].map((f) => (
              <button
                key={f.v}
                data-testid={`fresh-${f.v}`}
                onClick={() => { setFreshDays(f.v); load(1, query, tag, workType, country, city, minSalary, f.v, userTz, tzDelta); }}
                className={`nb-pill ${freshDays === f.v ? "bg-[#eab308] text-black" : "bg-white"}`}
              >{f.l}</button>
            ))}
          </div>
        </div>

        {/* Row 4: timezone */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[auto,1fr,auto] gap-3 md:items-center">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-600">
            <Clock className="w-3.5 h-3.5" /> Timezone
          </div>
          <div className="flex items-center gap-2 border-2 border-black rounded-lg px-3">
            <select
              data-testid="jobs-timezone"
              value={userTz}
              onChange={(e) => {
                const v = e.target.value;
                setUserTz(v);
                setTzManualOverride(!!v); // clearing tz allows the next city pick to suggest again
                load(1, query, tag, workType, country, city, minSalary, freshDays, v, tzDelta);
              }}
              className="w-full py-2.5 outline-none bg-transparent font-medium"
            >
              <option value="">Any timezone</option>
              {(() => {
                const bTz = typeof window !== "undefined" ? window.__browserTz : "";
                const showCustom = bTz && !TIMEZONES.find((t) => t.value === bTz);
                return showCustom ? <option value={bTz}>{`${bTz} (yours)`}</option> : null;
              })()}
              {TIMEZONES.map((t) => {
                const yours = typeof window !== "undefined" && window.__browserTz === t.value;
                return <option key={t.value} value={t.value}>{yours ? `${t.label} · yours` : t.label}</option>;
              })}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-600 mr-1">Overlap</span>
            {[3, 6, 9, 12].map((h) => (
              <button
                key={h}
                data-testid={`tz-delta-${h}`}
                onClick={() => { setTzDelta(h); if (userTz) load(1, query, tag, workType, country, city, minSalary, freshDays, userTz, h); }}
                className={`nb-pill ${tzDelta === h ? "bg-[#4338ca] text-white" : "bg-white"}`}
              >±{h}h</button>
            ))}
          </div>
        </div>

        {/* Row 5: languages you speak */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-600 mr-1">
            <Languages className="w-3.5 h-3.5" /> Languages you speak
          </div>
          <span className="nb-pill bg-black text-white text-[10px]">English (always)</span>
          {LANGUAGE_OPTIONS.map((l) => (
            <button
              key={l}
              data-testid={`lang-${l.toLowerCase()}`}
              onClick={() => toggleLang(l)}
              disabled={includeAllLangs}
              className={`nb-pill ${includeAllLangs ? "opacity-40 cursor-not-allowed" : ""} ${spokenLangs.includes(l) ? "bg-[#22c55e] text-white" : "bg-white"}`}
            >{l}</button>
          ))}
          {spokenLangs.length > 0 && !includeAllLangs && (
            <button data-testid="lang-clear" onClick={() => { setSpokenLangs([]); load(1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta, [], includeAllLangs); }}
              className="nb-pill bg-[#ec4899] text-white">clear</button>
          )}
          <label data-testid="lang-include-all-toggle" className="nb-pill bg-white inline-flex items-center gap-1.5 cursor-pointer select-none ml-auto">
            <input type="checkbox" className="accent-black" checked={includeAllLangs}
              onChange={(e) => { const v = e.target.checked; setIncludeAllLangs(v); load(1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta, spokenLangs, v); }} />
            <span className="text-[11px] font-bold">Show roles in any language</span>
          </label>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <span className="text-xs text-gray-500 font-bold uppercase tracking-widest self-center">Quick tags:</span>
          {commonTags.map((t) => (
            <button key={t} data-testid={`tag-${t}`} onClick={() => { setTag(t); load(1, query, t, workType, country, city, minSalary, freshDays, userTz, tzDelta); }}
              className={`nb-pill ${tag === t ? "bg-[#4338ca] text-white" : "bg-white"}`}>{t}</button>
          ))}
          {tag && <button onClick={() => { setTag(""); load(1, query, "", workType, country, city, minSalary, freshDays, userTz, tzDelta); }} className="nb-pill bg-[#ec4899] text-white">clear</button>}
        </div>

        {user && (
          <div className="mt-4 pt-4 border-t-2 border-dashed border-black/30 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-600">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Fit filter
            </div>
            {hasResume ? (
              <>
                <span className="text-sm text-gray-700">Only show jobs matching your saved resume by at least:</span>
                <div className="flex items-center gap-2">
                  {[0, 40, 60, 75].map((n) => (
                    <button key={n} data-testid={`min-score-${n}`} onClick={() => setMinScore(n)}
                      className={`nb-pill ${minScore === n ? "bg-[#22c55e] text-white" : "bg-white"}`}>
                      {n === 0 ? "All" : `${n}%+`}
                    </button>
                  ))}
                </div>
                <span className="ml-auto text-xs text-gray-500">Showing {sortedJobs.length} / {jobs.length}</span>
              </>
            ) : (
              <>
                <span className="text-sm text-gray-700">Save your resume to unlock instant fit scores on every job.</span>
                <Link to="/tailor" data-testid="jobs-save-resume-cta" className="nb-btn bg-[#ec4899] text-white px-3 py-1.5 text-sm">Save my resume</Link>
              </>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : sortedJobs.length === 0 ? (
        <div className="nb-card p-10 text-center">
          <p className="font-bold text-lg">No jobs match</p>
          <p className="text-gray-600 text-sm mt-1">{hasResume && minScore > 0 ? "Lower the fit threshold or clear filters." : "Try a different search, tag, or location."}</p>
        </div>
      ) : (
        <div data-testid="jobs-grid" className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {sortedJobs.map((j, i) => (
            <motion.div key={j.id} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              transition={{ delay: Math.min(i * 0.02, 0.3) }}
              className="nb-card p-6 flex flex-col">
              <div className="flex items-start gap-3">
                <CompanyLogo company={j.company} size={48} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="text-xs font-bold uppercase tracking-widest text-gray-500 truncate">{j.company}</div>
                        {user && j.company && (() => {
                          const key = companyKey(j.company);
                          const starred = watchlist.has(key);
                          return (
                            <button
                              data-testid={`watch-${j.id}`}
                              onClick={(e) => { e.preventDefault(); toggleWatch(j.company); }}
                              title={starred ? "Unstar company" : "Star company — get pinged when they post"}
                              className={`shrink-0 ${starred ? "text-[#eab308]" : "text-gray-300 hover:text-[#eab308]"}`}
                            >
                              <Star className={`w-4 h-4 ${starred ? "fill-[#eab308]" : ""}`} />
                            </button>
                          );
                        })()}
                      </div>
                      <h3 className="text-lg font-bold leading-snug mt-0.5 line-clamp-2">{j.title}</h3>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {j.remote && <span className="nb-pill bg-[#22c55e] text-white">Remote</span>}
                      {hasResume && <MatchBadge score={j.match_score} />}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
                <div className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.location || "Anywhere"}</div>
                <div className={`flex items-center gap-1 ${j.salary_min_usd > 0 ? "font-bold text-[#0caa41]" : "text-gray-400"}`}>
                  <DollarSign className="w-3.5 h-3.5" />
                  {j.salary_min_usd > 0 ? `${(j.salary_min_usd/1000).toFixed(0)}k+` : "Salary not listed"}
                </div>
                {j.created_at && (() => {
                  const d = new Date(j.created_at);
                  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
                  if (isNaN(days) || days < 0) return null;
                  const label = days === 0 ? "today" : days === 1 ? "1d ago" : `${days}d ago`;
                  return <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{label}</div>;
                })()}
                {j.job_types?.slice(0, 2).map((t) => (
                  <div key={t} className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{t}</div>
                ))}
              </div>
              <p className="mt-3 text-sm text-gray-700 line-clamp-3">{cleanText(j.description)}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(j.required_languages || []).map((l) => (
                  <span key={l} className="nb-pill bg-[#eab308] text-black text-[10px] inline-flex items-center gap-1"><Languages className="w-3 h-3" />{l}</span>
                ))}
                {(j.tags || []).slice(0, 4).map((t) => (<span key={t} className="nb-pill bg-white text-[10px]">{t}</span>))}
              </div>
              <div className="mt-auto pt-5 flex items-center justify-between gap-2 border-t-2 border-dashed border-black">
                <Link to={`/jobs/${j.id}`} data-testid={`job-view-${j.id}`} className="nb-btn bg-white px-4 py-2 text-sm">View details</Link>
                <button
                  data-testid={`job-apply-${j.id}`}
                  onClick={async (e) => {
                    e.preventDefault();
                    // Open a real tab up-front so popup blockers don't kill it during the await.
                    // NOTE: passing "noopener" causes window.open to return null, so we can't
                    // rewrite the URL later. We open without noopener and sever opener manually.
                    const tab = window.open("", "_blank");
                    if (tab) { try { tab.opener = null; } catch { /* noop */ } }
                    const go = (url) => {
                      if (!url) return;
                      if (tab && !tab.closed) tab.location.href = url;
                      else window.location.href = url;
                    };
                    try {
                      const { data } = await api.get(`/jobs/${j.id}/apply`);
                      go(data && data.url ? data.url : j.url);
                    } catch (err) {
                      go(j.url);
                    }
                  }}
                  className="nb-btn bg-[#4338ca] text-white px-4 py-2 text-sm"
                >Apply</button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <div className="mt-8 flex items-center justify-center gap-3">
        <button data-testid="jobs-prev" disabled={page <= 1} onClick={() => load(page - 1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta)}
          className="nb-btn bg-white px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
        <span className="text-sm font-bold">Page {page}</span>
        <button data-testid="jobs-next" disabled={!hasMore} onClick={() => load(page + 1, query, tag, workType, country, city, minSalary, freshDays, userTz, tzDelta)}
          className="nb-btn bg-white px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
      </div>
    </div>
  );
}
